/**
 * Flow Session Context
 * Shared context definition and the useFlowSession consumer hook.
 * Separated from the Provider component to avoid circular dependencies
 * and fast-refresh issues when hooks import useFlowSession.
 */

import { createContext, useContext } from "react";

import type { FlowSession } from "@/types/session.types";

export interface FlowSessionContextValue {
  session: FlowSession | null;
  isEmbeddedMode: boolean;
  /** Initialize session after bootstrap redemption */
  setSession: (session: FlowSession) => void;
  /** Mark the current flow as completed */
  completeFlow: (result?: Record<string, unknown>) => void;
  /** User chose to exit/cancel the flow */
  exitFlow: (reason?: "user_cancelled" | "navigation" | "error") => void;
  /** Session has expired */
  expireFlow: () => void;
}

export const FlowSessionContext = createContext<FlowSessionContextValue | null>(
  null
);

export function useFlowSession(): FlowSessionContextValue {
  const ctx = useContext(FlowSessionContext);
  if (!ctx) {
    // Return null-safe defaults â€” allows components like FlowErrorPage to
    // render safely even when called outside the provider (e.g. error boundary
    // fallback above FlowSessionProvider in the tree).
    return {
      session: null,
      isEmbeddedMode: false,
      setSession: () => {},
      completeFlow: () => {},
      exitFlow: () => {},
      expireFlow: () => {}
    };
  }
  return ctx;
}
